/* eslint-disable @typescript-eslint/no-explicit-any */
import { Row, Col } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { NewApplicationsData } from 'data/newapplications';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import orderOutService from 'service/orderOut';
import orderService from 'service/order';
import Button from 'components/base/Button';
import SupperModal from 'components/modals/SupperModal';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';

interface NewApplicationDataTableProps {
  setOpenFolder: (show: boolean) => void;
}

const NewApplicationDataTable = ({
  setOpenFolder
}: NewApplicationDataTableProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const legalEntityName = useSelector(
    (state: any) => state?.auth?.legal_entity?.name
  );

  const [openSupperModal, setOpenSupperModal] = useState(false);
  const [confirmToDelete, setConfirmToDelete] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null
  );
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isFetching } = useQuery(
    ['GET_ORDER_OUT', currentPage],
    () => {
      const res = orderOutService
        .getList({
          offset: pageToOffset(currentPage, 10),
          limit: 10,
          state: 1
        })
        .then((res: any) => {
          return res;
        });
      return res;
    },
    {
      enabled: !!currentPage
    }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  const tableData: NewApplicationsData[] = useMemo(() => {
    const resultData =
      data?.orders?.map((items: any) => {
        return {
          orders: items?.order_number,
          sender: items?.legal_entity?.name,
          recipient: legalEntityName,
          currency: items?.currency?.name?.[i18n?.language],
          currency_pid: items?.currency_pid || 0,
          date_create: new Date(items?.date_create)
            .toLocaleDateString('en-GB')
            .replace(/\//g, '-'),
          sender_pid: items?.sender_pid,
          guid: items?.id,
          gupid: items?.pid || 0
        };
      }) ?? [];

    return resultData;
  }, [data, t, i18n?.language]);

  const handleDelete = (pid: number) => {
    setSelectedInvoiceId(pid);
    setOpenSupperModal(true);
  };

  const newApplicationsTableColumns: ColumnDef<NewApplicationsData>[] = [
    {
      id: 'action',
      cell: rowData => {
        return (
          <Row>
            <Col className="d-flex gap-2">
              <Button
                variant="hover"
                className="p-2"
                onClick={() => {
                  navigate(
                    `/applications/outgoing/filter/newapplications/${rowData?.row?.original?.gupid}`,
                    {
                      state: {
                        sender_pid: rowData?.row?.original?.sender_pid,
                        currency_pid: rowData?.row?.original?.currency_pid
                      }
                    }
                  );
                  setOpenFolder(false);
                }}
              >
                <FeatherIcon icon="plus" className="cursor-pointer" size={20} />
              </Button>
              <Button
                variant="hover"
                className="p-2"
                onClick={() => handleDelete(rowData?.row?.original?.gupid)}
              >
                <FeatherIcon
                  icon="trash-2"
                  className="cursor-pointer text-danger"
                  size={20}
                />
              </Button>
            </Col>
          </Row>
        );
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-end' }
      }
    },
    {
      accessorKey: 'orders',
      header: t('order_number'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'sender',
      header: t('sender'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'recipient',
      header: t('recipient'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'date_create',
      header: t('date_create'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: tableData,
    columns: newApplicationsTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if (selectedInvoiceId && confirmToDelete) {
      orderService.deleteOrder(selectedInvoiceId).finally(() => {
        refetch();
        setOpenSupperModal(false);
        setConfirmToDelete(false);
        setSelectedInvoiceId(null);
      });
    }
  }, [confirmToDelete, selectedInvoiceId, refetch]);

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            isLoading={loading}
          />
          <AdvanceTableCPagination
            count={pageCount}
            page={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </AdvanceTableProvider>

      <SupperModal
        show={openSupperModal}
        setShow={setOpenSupperModal}
        setConfirm={setConfirmToDelete}
        title={t('select_application')}
        bodyText={t('you_want_delete')}
      />
    </div>
  );
};

export default NewApplicationDataTable;
